// Short-lived last-scan stash for Collection review after the scanner closes.
// Survives route changes within the tab; not synced across devices.

import type { ScanSessionAdd } from './scanDecision';

export const LAST_SCAN_SESSION_STORAGE_KEY = 'riftbound.lastScanSession';
export const LAST_SCAN_SESSION_CHANGED_EVENT = 'riftbound:last-scan-changed';

/** Drop stale sessions so a long-lived tab does not keep an ancient review chip. */
export const LAST_SCAN_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export interface LastScanSession {
  id: string;
  endedAt: number;
  adds: ScanSessionAdd[];
  tipDismissed?: boolean;
  reviewDismissed?: boolean;
}

function notifyChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LAST_SCAN_SESSION_CHANGED_EVENT));
}

function isScanSessionAdd(value: unknown): value is ScanSessionAdd {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<ScanSessionAdd>;
  return (
    typeof row.id === 'string' &&
    typeof row.cardId === 'string' &&
    typeof row.name === 'string' &&
    typeof row.setCode === 'string' &&
    typeof row.number === 'string' &&
    typeof row.isFoil === 'boolean' &&
    typeof row.quantity === 'number' &&
    typeof row.at === 'number'
  );
}

function isLastScanSession(value: unknown): value is LastScanSession {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<LastScanSession>;
  return (
    typeof row.id === 'string' &&
    typeof row.endedAt === 'number' &&
    Array.isArray(row.adds) &&
    row.adds.every(isScanSessionAdd)
  );
}

export function lastScanSessionCardIds(session: LastScanSession | null | undefined): string[] {
  if (!session?.adds.length) return [];
  return [...new Set(session.adds.map((add) => add.cardId))];
}

/** Max `at` per cardId for Recently scanned / Just scanned ordering. */
export function lastScanSessionAtByCardId(
  session: LastScanSession | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!session) return out;
  for (const add of session.adds) {
    const prev = out[add.cardId] ?? 0;
    if (add.at > prev) out[add.cardId] = add.at;
  }
  return out;
}

export function lastScanSessionTotalQuantity(session: LastScanSession | null | undefined): number {
  if (!session) return 0;
  return session.adds.reduce((sum, add) => sum + add.quantity, 0);
}

export function readLastScanSession(now = Date.now()): LastScanSession | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LAST_SCAN_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isLastScanSession(parsed)) {
      sessionStorage.removeItem(LAST_SCAN_SESSION_STORAGE_KEY);
      return null;
    }
    if (now - parsed.endedAt > LAST_SCAN_SESSION_TTL_MS) {
      sessionStorage.removeItem(LAST_SCAN_SESSION_STORAGE_KEY);
      return null;
    }
    if (parsed.adds.length === 0) {
      sessionStorage.removeItem(LAST_SCAN_SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeLastScanSession(session: LastScanSession | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (!session || session.adds.length === 0) {
      sessionStorage.removeItem(LAST_SCAN_SESSION_STORAGE_KEY);
      notifyChanged();
      return;
    }
    sessionStorage.setItem(LAST_SCAN_SESSION_STORAGE_KEY, JSON.stringify(session));
    notifyChanged();
  } catch {
    /* quota / private mode */
  }
}

/** Persist a completed scan session for Collection review. Empty adds clear the stash. */
export function saveLastScanSession(adds: ScanSessionAdd[], now = Date.now()): LastScanSession | null {
  if (!adds.length) {
    clearLastScanSession();
    return null;
  }
  const session: LastScanSession = {
    id: `${now}-${Math.random().toString(36).slice(2, 9)}`,
    endedAt: now,
    adds: adds.map((add) => ({ ...add })),
    tipDismissed: false,
    reviewDismissed: false,
  };
  writeLastScanSession(session);
  return session;
}

export function clearLastScanSession(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (!sessionStorage.getItem(LAST_SCAN_SESSION_STORAGE_KEY)) return;
    sessionStorage.removeItem(LAST_SCAN_SESSION_STORAGE_KEY);
    notifyChanged();
  } catch {
    /* ignore */
  }
}

export function dismissLastScanTip(): void {
  const session = readLastScanSession();
  if (!session || session.tipDismissed) return;
  writeLastScanSession({ ...session, tipDismissed: true });
}

export function dismissLastScanReview(): void {
  const session = readLastScanSession();
  if (!session || session.reviewDismissed) return;
  writeLastScanSession({ ...session, reviewDismissed: true });
}

/** Tip is pending when a non-dismissed session exists (caller also checks route). */
export function hasPendingLastScanTip(session = readLastScanSession()): boolean {
  return Boolean(session && !session.tipDismissed && session.adds.length > 0);
}

export function hasActiveLastScanReview(session = readLastScanSession()): boolean {
  return Boolean(session && !session.reviewDismissed && session.adds.length > 0);
}

export function subscribeLastScanSessionChanged(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(LAST_SCAN_SESSION_CHANGED_EVENT, listener);
  return () => window.removeEventListener(LAST_SCAN_SESSION_CHANGED_EVENT, listener);
}
